import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { useEffectOnce } from "@lib-shared/hooks";
import { getButtonAnalytics } from "@lib-shared/lib/get-analytics-tags";
import { Button, ButtonToolbar, Form, FormWrapper, Input, SubmitButton } from "@shared-forms";
import { Row } from "@shared/components";
import { isRequiredNumberString } from "@shared/lib";
import { AppContext } from "core/app-context";
import { Page } from "core/constants";
import FormCallbackFunc from "core/models/form-callback-func";
import OtpDetails from "core/models/validate-details";

type OtpFormProps = {
    onSubmit: FormCallbackFunc,
    onResend: FormCallbackFunc
}

const OtpForm = observer((props: OtpFormProps) => {
    const store = useContext(AppContext);
    const { onResend, onSubmit } = props;
    const [enableResend, setEnableResend] = useState(false);
    const [showResendCountdown, setShowResendCountdown] = useState(false);
    const [countdown, setCountdown] = useState("");
    const navigate = useNavigate();

    const formGroupCssName = "col-md-12 form-floating mb-3";

    const config = {
        otp: isRequiredNumberString("Please enter your OTP number", 5)
    };

    const submitForm = (data: OtpDetails) => {
        store.updateOtpDetails(data);

        onSubmit();
    };

    const getCountdown = () => {
        const resend = new Date(store.otpLastSent as Date);
        resend.setSeconds(resend.getSeconds() + 30);

        const nowTime = new Date().getTime();
        const resendTime = resend.getTime();

        let diff = (resendTime - nowTime) / 1000;
        diff = Math.round(Math.abs(diff));

        const value = nowTime > resendTime ? 0 : diff;
        return value;
    }

    const updateCountdownMessage = (message: string) => {
        setCountdown(message);
        setShowResendCountdown(!!message);
    }

    const updateEnableResend = () => {
        let enabled = !!store.otpLastSent;

        if (!enabled) {
            setEnableResend(enabled);
            updateCountdownMessage("");
            return;
        }

        const countdown = getCountdown();

        enabled = countdown <= 0;

        const countdownMessage = !enabled
            ? `${countdown} seconds until you can resend otp`
            : "";

        updateCountdownMessage(countdownMessage);
        setEnableResend(enabled);

        if (!enabled) {
            setTimeout(updateEnableResend, 1000)
        }
    };

    const resendOtp = async () => {
        await onResend();
        updateEnableResend();
    }

    useEffectOnce(() => {
        if (!store.searchDetails?.searchValue) {
            navigate(Page.search);
        }

        store.updatePage(Page.otp);

        updateEnableResend();
    });

    const analytics = getButtonAnalytics("OTP Number", "Submit");
    const resendAnalytics = getButtonAnalytics("OTP Number", "Resend OTP");

    return (
        <Form defaultValues={store.otpDetails} onSubmit={submitForm}>
            <FormWrapper>
                <Row>
                    {store.otpError && <p className="error">{store.otpError}</p>}

                    <Input
                        cssName={formGroupCssName}
                        name="otp"
                        label="OTP number"
                        fieldOptions={config.otp} />

                    {showResendCountdown && <small>{countdown}</small>}
                </Row>
            </FormWrapper>

            <ButtonToolbar>
                <Button
                    colour="secondary"
                    disabled={!enableResend}
                    className="next"
                    type="button"
                    text="Resend OTP"
                    {...resendAnalytics}
                    onClick={resendOtp} />

                <SubmitButton
                    color="primary"
                    disabled={store.isLoading}
                    {...analytics}
                    text="Submit" />
            </ButtonToolbar>
        </Form>
    );
});

export default OtpForm;
